import { useEffect, useState } from 'react'
import type { AccountInfo } from '@azure/msal-browser'
import { getActiveAccount, initializeAuth, signIn, signOut } from './auth'
import { Chat } from './components/Chat'
import { Header } from './components/Header'
import { HistoryPanel } from './components/HistoryPanel'
import { SignIn } from './components/SignIn'
import { useChat } from './hooks/useChat'

export function App() {
  const [account, setAccount] = useState<AccountInfo | null>(null)
  const [ready, setReady] = useState(false)
  const [signingIn, setSigningIn] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  useEffect(() => {
    initializeAuth()
      .then(() => setAccount(getActiveAccount()))
      .catch((err: unknown) =>
        setAuthError(err instanceof Error ? err.message : 'Sign-in failed.'),
      )
      .finally(() => setReady(true))
  }, [])

  const chat = useChat(account ? (account.homeAccountId || account.username) : null)

  if (!ready) {
    return (
      <div className="boot" role="status" aria-live="polite">
        <span className="spinner spinner--lg" aria-hidden />
      </div>
    )
  }

  if (!account) {
    return (
      <>
        {authError && (
          <div className="alert alert--top" role="alert">
            {authError}
          </div>
        )}
        <SignIn
          busy={signingIn}
          onSignIn={() => {
            setSigningIn(true)
            void signIn().catch((err: unknown) => {
              setSigningIn(false)
              setAuthError(err instanceof Error ? err.message : 'Sign-in failed.')
            })
          }}
        />
      </>
    )
  }

  return (
    <div className="app">
      <Header
        userName={account.name ?? account.username}
        onSignOut={() => void signOut()}
        onNewChat={chat.reset}
        onShowHistory={() => setHistoryOpen(true)}
      />
      <Chat chat={chat} />
      {historyOpen && (
        <HistoryPanel
          conversations={chat.history}
          activeId={chat.activeId}
          onOpen={chat.openConversation}
          onDelete={chat.removeConversation}
          onNewChat={chat.reset}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  )
}
